// src/components/billing/CreateQuotationModal.jsx
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    collection,
    addDoc,
    Timestamp,
    doc,
    getDoc,
    updateDoc,
    getDocs,
    query,
    where
} from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useNotification } from '../../contexts/NotificationContext';
import { getProducts } from '../../services/inventoryService';
import { createQuotation } from '../../services/billingService';

// Customer Management Modal Component
const CustomerModal = ({ isOpen, onClose, onSave, editingCustomer = null }) => {
    const { vendorProfile } = useAuth();
    const { showAlert } = useNotification();
    const [loading, setLoading] = useState(false);
    const [customerData, setCustomerData] = useState({
        name: '',
        email: '',
        phone: '',
        address: '',
        gstNumber: ''
    });

    useEffect(() => {
        if (editingCustomer) {
            setCustomerData(editingCustomer);
        } else {
            setCustomerData({
                name: '',
                email: '',
                phone: '',
                address: '',
                gstNumber: ''
            });
        }
    }, [editingCustomer]);

    const handleInputChange = (field, value) => {
        setCustomerData(prev => ({ ...prev, [field]: value }));
    };

    const handleSave = async () => {
        if (!customerData.name.trim()) {
            showAlert('Customer name is required', 'error');
            return;
        }

        setLoading(true);
        try {
            const customerDoc = {
                ...customerData,
                vendorId: vendorProfile.uid,
                createdAt: Timestamp.fromDate(new Date()),
                updatedAt: Timestamp.fromDate(new Date())
            };

            if (editingCustomer && editingCustomer.id) {
                await updateDoc(doc(db, 'customers', editingCustomer.id), {
                    ...customerDoc,
                    createdAt: editingCustomer.createdAt
                });
                showAlert('Customer updated successfully', 'success');
            } else {
                const docRef = await addDoc(collection(db, 'customers'), customerDoc);
                customerDoc.id = docRef.id;
                showAlert('Customer added successfully', 'success');
            }

            onSave(customerDoc);
            onClose();
        } catch (error) {
            console.error('Error saving customer:', error);
            showAlert('Failed to save customer', 'error');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-50" onClick={onClose}></div>
            <div className="fixed inset-0 z-50 overflow-y-auto">
                <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                    <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                        <div className="bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                            <div className="sm:flex sm:items-start">
                                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left w-full">
                                    <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">
                                        {editingCustomer ? 'Edit Customer' : 'Add New Customer'}
                                    </h3>

                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                                            <input
                                                type="text"
                                                required
                                                value={customerData.name}
                                                onChange={(e) => handleInputChange('name', e.target.value)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                                placeholder="Enter customer name"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Email</label>
                                            <input
                                                type="email"
                                                value={customerData.email}
                                                onChange={(e) => handleInputChange('email', e.target.value)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                                placeholder="customer@example.com"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Phone</label>
                                            <input
                                                type="tel"
                                                value={customerData.phone}
                                                onChange={(e) => handleInputChange('phone', e.target.value)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                                placeholder="+91 9876543210"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">GST Number</label>
                                            <input
                                                type="text"
                                                value={customerData.gstNumber}
                                                onChange={(e) => handleInputChange('gstNumber', e.target.value)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                                placeholder="Enter GST number"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700">Address</label>
                                            <textarea
                                                rows={3}
                                                value={customerData.address}
                                                onChange={(e) => handleInputChange('address', e.target.value)}
                                                className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                                placeholder="Customer address"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                            <button
                                type="button"
                                onClick={handleSave}
                                disabled={loading}
                                className="inline-flex w-full justify-center rounded-md bg-purple-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-purple-500 disabled:opacity-50 sm:ml-3 sm:w-auto"
                            >
                                {loading ? 'Saving...' : (editingCustomer ? 'Update' : 'Add')} Customer
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// Product Selection Modal Component
const ProductSelectionModal = ({
    isOpen,
    onClose,
    products,
    onSelectProducts,
    selectedProducts = [],
    onAddManualItem,
    onNavigateToAddProduct,
    brands = []
}) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [filteredProducts, setFilteredProducts] = useState(products);
    const [tempSelectedProducts, setTempSelectedProducts] = useState([...selectedProducts]);

    const getBrandName = (productBrand) => {
        if (!productBrand) return 'N/A';
        if (!brands || brands.length === 0) {
            if (typeof productBrand === 'string') return productBrand;
            if (Array.isArray(productBrand) && productBrand.length > 0) return productBrand[0];
            if (productBrand && typeof productBrand === 'object') return productBrand.name || 'N/A';
            return 'N/A';
        }
        if (typeof productBrand === 'string') {
            const brandObj = brands.find(b => b.id === productBrand || (b.name && b.name.toLowerCase() === productBrand.toLowerCase()));
            return brandObj?.name || productBrand;
        } else if (Array.isArray(productBrand) && productBrand.length > 0) {
            const firstBrand = productBrand[0];
            const brandObj = brands.find(b => b.id === firstBrand || (b.name && b.name.toLowerCase() === firstBrand.toLowerCase()));
            return brandObj?.name || firstBrand;
        } else if (productBrand && typeof productBrand === 'object') {
            return productBrand.name || 'N/A';
        }
        return 'N/A';
    };

    const getStockBadge = (stock, reorderLevel = 10) => {
        const stockNum = Number(stock) || 0;
        const reorderNum = Number(reorderLevel) || 10;

        if (stockNum <= 0) {
            return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">Out of Stock</span>;
        } else if (stockNum <= reorderNum) {
            return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">Low: {stockNum}</span>;
        }
        return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{stockNum}</span>;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const getDisplayImageUrl = (product) => {
        if (product.imageUrls && Array.isArray(product.imageUrls) && product.imageUrls.length > 0) {
            const validUrls = product.imageUrls.filter(url => url && typeof url === 'string');
            if (validUrls.length > 0) return validUrls[0];
        }
        return null;
    };

    useEffect(() => {
        if (searchTerm.trim() === '') {
            setFilteredProducts(products);
        } else {
            const filtered = products.filter(product =>
                product.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                (product.partNumber && product.partNumber.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (product.sku && product.sku.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (product.description && product.description.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (getBrandName(product.brand || product.brands).toLowerCase().includes(searchTerm.toLowerCase()))
            );
            setFilteredProducts(filtered);
        }
    }, [searchTerm, products, brands]);

    const handleQuantityChange = (productId, quantity) => {
        const parsedQuantity = quantity === '' ? '' : Math.max(1, parseInt(quantity) || 1);

        setTempSelectedProducts(prev => {
            const existing = prev.find(item => item.productId === productId);
            if (existing) {
                return prev.map(item =>
                    item.productId === productId ? { ...item, quantity: parsedQuantity } : item
                );
            }
            const product = products.find(p => p.id === productId);
            return [...prev, {
                productId,
                name: product.name,
                quantity: parsedQuantity === '' ? 1 : parsedQuantity,
                rate: product.price || product.sellingPrice || 0,
                hsnCode: product.hsnCode || '',
                gstRate: 18
            }];
        });
    };

    const handleToggleProduct = (product) => {
        setTempSelectedProducts(prev => {
            const existing = prev.find(item => item.productId === product.id);
            if (existing) {
                return prev.filter(item => item.productId !== product.id);
            }
            return [...prev, {
                productId: product.id,
                name: product.name,
                quantity: 1,
                rate: product.price || product.sellingPrice || 0,
                hsnCode: product.hsnCode || '',
                gstRate: 18
            }];
        });
    };

    const handleConfirm = () => {
        onSelectProducts(tempSelectedProducts);
        onClose();
    };

    const handleAddManualItemClick = () => {
        onAddManualItem();
        onClose();
    };

    const handleAddNewProductClick = () => {
        onNavigateToAddProduct();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <>
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-40" onClick={onClose}></div>
            <div className="fixed inset-y-0 right-0 w-[480px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out">
                <div className="h-full flex flex-col">
                    <div className="px-6 py-4 border-b border-gray-200">
                        <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">Select Products</h3>
                            <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>

                        <div className="mt-4 flex space-x-2">
                            <button
                                type="button"
                                onClick={handleAddManualItemClick}
                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                            >
                                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Manual Item
                            </button>
                            <button
                                type="button"
                                onClick={handleAddNewProductClick}
                                className="flex-1 inline-flex items-center justify-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                            >
                                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add New Product
                            </button>
                        </div>

                        <div className="mt-4">
                            <div className="relative">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                    </svg>
                                </div>
                                <input
                                    type="text"
                                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    placeholder="Search products by name, part number, brand..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="flex-1 overflow-y-auto">
                        {filteredProducts.length === 0 ? (
                            <div className="p-6 text-center text-gray-500">
                                {searchTerm ? 'No products found matching your search.' : 'No products available.'}
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-200">
                                {filteredProducts.map((product) => (
                                    <div
                                        key={product.id}
                                        className={`p-4 cursor-pointer transition-colors ${tempSelectedProducts.some(item => item.productId === product.id)
                                            ? 'bg-purple-50 border-l-4 border-purple-500'
                                            : 'hover:bg-gray-50'
                                            }`}
                                        onClick={() => handleToggleProduct(product)}
                                    >
                                        <div className="flex items-start space-x-3">
                                            <div className="flex-shrink-0 h-16 w-16 relative">
                                                {getDisplayImageUrl(product) ? (
                                                    <>
                                                        <img
                                                            className="h-16 w-16 rounded-md object-cover border border-gray-200"
                                                            src={getDisplayImageUrl(product)}
                                                            alt={product.name}
                                                            onError={(e) => {
                                                                e.target.style.display = 'none';
                                                                e.target.nextElementSibling.style.display = 'flex';
                                                            }}
                                                        />
                                                        <div className={`h-16 w-16 rounded-md bg-gray-200 items-center justify-center text-gray-500 border border-gray-200 hidden absolute inset-0`}>
                                                            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                            </svg>
                                                        </div>
                                                    </>
                                                ) : (
                                                    <div className="h-16 w-16 rounded-md bg-gray-200 flex items-center justify-center text-gray-500 border border-gray-200">
                                                        <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-1 min-w-0">
                                                <h4 className="text-sm font-medium text-gray-900 truncate" title={product.name}>
                                                    {product.name || 'Unnamed Product'}
                                                </h4>

                                                <div className="text-sm text-gray-500 mt-1">
                                                    {product.partNumber && `Part: ${product.partNumber}`}
                                                    {product.partNumber && product.sku && ' • '}
                                                    {product.sku && `SKU: ${product.sku}`}
                                                </div>

                                                {(product.brand || product.brands) && (
                                                    <div className="text-xs text-gray-600 mt-1">
                                                        Brand: {getBrandName(product.brand || product.brands)}
                                                    </div>
                                                )}

                                                <div className="mt-2 flex items-center justify-between">
                                                    <div className="flex items-center space-x-2">
                                                        {getStockBadge(product.stock || 0, product.reorderLevel)}
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {formatCurrency(product.price || product.sellingPrice || 0)}
                                                        </span>
                                                    </div>
                                                </div>

                                                {tempSelectedProducts.some(item => item.productId === product.id) && (
                                                    <div className="mt-3 flex items-center space-x-2">
                                                        <label className="text-xs text-gray-600 font-medium">Quantity:</label>
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            max={product.stock || 999}
                                                            value={tempSelectedProducts.find(item => item.productId === product.id)?.quantity || ''}
                                                            onChange={(e) => handleQuantityChange(product.id, e.target.value)}
                                                            onBlur={(e) => {
                                                                if (e.target.value === '' || e.target.value === '0') {
                                                                    handleQuantityChange(product.id, '1');
                                                                }
                                                            }}
                                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500"
                                                            onClick={(e) => e.stopPropagation()}
                                                            placeholder="1"
                                                        />
                                                        <span className="text-xs text-gray-500">
                                                            of {product.stock || 0} available
                                                        </span>
                                                    </div>
                                                )}
                                            </div>

                                            <div className="flex-shrink-0">
                                                {tempSelectedProducts.some(item => item.productId === product.id) ? (
                                                    <div className="text-purple-600">
                                                        <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                ) : (
                                                    <div className="text-gray-300">
                                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                        </svg>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="border-t border-gray-200 px-6 py-4">
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-sm text-gray-600">
                                {tempSelectedProducts.length} product{tempSelectedProducts.length !== 1 ? 's' : ''} selected
                            </span>
                            {tempSelectedProducts.length > 0 && (
                                <button
                                    type="button"
                                    onClick={() => setTempSelectedProducts([])}
                                    className="text-sm text-purple-600 hover:text-purple-800 font-medium"
                                >
                                    Clear All
                                </button>
                            )}
                        </div>
                        <button
                            type="button"
                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-purple-600 text-base font-medium text-white hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-colors sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                            onClick={handleConfirm}
                            disabled={tempSelectedProducts.length === 0}
                        >
                            Confirm Selection ({tempSelectedProducts.length})
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

const CreateQuotationModal = ({ onClose, onSubmit, onNavigateToAddProduct, isModal = true }) => {
    const { vendorProfile } = useAuth();
    const { showAlert } = useNotification();

    const [step, setStep] = useState(1);

    // Generate quotation number once and store it
    const [quotationNumber] = useState(() => {
        const prefix = 'QUO';
        const date = new Date();
        const timestamp = date.getTime().toString().slice(-6);
        return `${prefix}-${date.getFullYear()}-${timestamp}`;
    });

    const [formData, setFormData] = useState({
        customerName: '',
        customerEmail: '',
        customerPhone: '',
        customerAddress: '',
        customerGST: '',
        items: [], // Remove the default manual item
        subtotal: 0,
        quotationType: 'with_gst',
        totalTaxAmount: 0,
        discount: 0,
        totalAmount: 0,
        quotationDate: new Date().toISOString().split('T')[0],
        notes: '',
        terms: 'This quotation is valid for 30 days from the date of issue. Prices are subject to change without notice.'
    });
    const [bankAccounts, setBankAccounts] = useState([]);
    const [products, setProducts] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
    const [showCustomerModal, setShowCustomerModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showProductModal, setShowProductModal] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        loadProducts();
        loadCustomers();
        loadBankAccounts();
    }, []);

    const loadProducts = async () => {
        try {
            if (vendorProfile?.uid) {
                const result = await getProducts(vendorProfile.uid, { pageSize: 100 });
                setProducts(result.products || []);
            }
        } catch (error) {
            console.error('Error loading products:', error);
            setError('Failed to load products');
        }
    };

    const loadCustomers = async () => {
        try {
            if (vendorProfile?.uid) {
                const customersQuery = query(
                    collection(db, 'customers'),
                    where('vendorId', '==', vendorProfile.uid)
                );
                const customersSnapshot = await getDocs(customersQuery);
                const customersList = customersSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                setCustomers(customersList);
            }
        } catch (error) {
            console.error('Error loading customers:', error);
        }
    };


    const loadBankAccounts = async () => {
        try {
            if (vendorProfile?.uid) {
                const bankCollection = collection(db, 'vendors', vendorProfile.uid, 'bankAccounts');
                const bankSnapshot = await getDocs(bankCollection);
                const accounts = bankSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                // Sort accounts to put primary account first
                accounts.sort((a, b) => {
                    if (a.isPrimary && !b.isPrimary) return -1;
                    if (!a.isPrimary && b.isPrimary) return 1;
                    return 0;
                });

                setBankAccounts(accounts);
                console.log('Loaded bank accounts:', accounts); // Debug log
            }
        } catch (error) {
            console.error('Error loading bank accounts:', error);
        }
    };

    const handleCustomerSearch = (value) => {
        setFormData(prev => ({ ...prev, customerName: value }));

        if (value.trim()) {
            const filtered = customers.filter(customer =>
                customer.name.toLowerCase().includes(value.toLowerCase()) ||
                (customer.phone && customer.phone.includes(value)) ||
                (customer.email && customer.email.toLowerCase().includes(value.toLowerCase()))
            );
            setFilteredCustomers(filtered);
            setShowCustomerDropdown(true);
        } else {
            setShowCustomerDropdown(false);
            setFormData(prev => ({
                ...prev,
                customerEmail: '',
                customerPhone: '',
                customerAddress: '',
                customerGST: ''
            }));
        }
    };

    const handleCustomerSelect = (customer) => {
        setFormData(prev => ({
            ...prev,
            customerName: customer.name,
            customerEmail: customer.email || '',
            customerPhone: customer.phone || '',
            customerAddress: customer.address || '',
            customerGST: customer.gstNumber || ''
        }));
        setShowCustomerDropdown(false);
    };

    const handleAddCustomer = () => {
        setShowCustomerModal(true);
    };

    const handleCustomerSaved = (savedCustomer) => {
        loadCustomers();
        if (savedCustomer) {
            handleCustomerSelect(savedCustomer);
        }
    };

    const calculateTotals = () => {
        const subtotal = formData.items.reduce((sum, item) => sum + (item.amount || 0), 0);
        const discountPercentage = parseFloat(formData.discount) || 0;
        const discountAmount = (subtotal * discountPercentage) / 100;
        const taxableAmount = subtotal - discountAmount;

        let totalTaxAmount = 0;
        let totalAmount = taxableAmount;

        if (formData.quotationType === 'with_gst') {
            totalTaxAmount = formData.items.reduce((sum, item) => {
                const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
                const itemTaxAmount = (itemTaxableAmount * (item.gstRate || 0)) / 100;
                return sum + itemTaxAmount;
            }, 0);
            totalAmount = taxableAmount + totalTaxAmount;
        }

        setFormData(prev => ({
            ...prev,
            subtotal,
            totalTaxAmount,
            totalAmount
        }));
    };

    useEffect(() => {
        calculateTotals();
    }, [formData.items, formData.discount, formData.quotationType]);

    const handleInputChange = (field, value) => {
        if (field === 'discount') {
            const newValue = Math.max(0, Math.min(100, parseFloat(value) || 0));
            setFormData(prev => ({ ...prev, [field]: newValue }));
        } else {
            setFormData(prev => ({ ...prev, [field]: value }));
        }
    };

    const handleItemChange = (index, field, value) => {
        const newItems = [...formData.items];
        if (field === 'quantity') {
            newItems[index][field] = parseInt(value) || 0;
        } else if (field === 'rate') {
            newItems[index][field] = parseFloat(value) || 0;
        } else if (field === 'gstRate') {
            newItems[index][field] = parseFloat(value) || 0;
        } else {
            newItems[index][field] = value;
        }

        newItems[index].amount = (newItems[index].quantity || 0) * (newItems[index].rate || 0);
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleProductModalOpen = () => {
        setShowProductModal(true);
    };

    const handleProductSelect = (selectedProducts) => {
        const newProductItems = selectedProducts.map(product => ({
            productId: product.productId,
            name: product.name,
            quantity: product.quantity || 1,
            rate: product.rate || 0,
            amount: (product.quantity || 1) * (product.rate || 0),
            isManual: false,
            hsnCode: product.hsnCode || '',
            gstRate: product.gstRate || 18
        }));

        // Keep existing manual items and add new product items
        setFormData(prev => ({
            ...prev,
            items: [
                ...prev.items.filter(item => item.isManual), // Keep manual items
                ...newProductItems // Add new product items
            ]
        }));
        setShowProductModal(false);
    };

    const addManualItem = () => {
        setFormData(prev => ({
            ...prev,
            items: [...prev.items, { productId: '', name: '', quantity: 1, rate: 0, amount: 0, isManual: true, hsnCode: '', gstRate: 18 }]
        }));
    };

    const addProductItem = () => {
        setShowProductModal(true);
    };

    const removeItem = (index) => {
        const newItems = formData.items.filter((_, i) => i !== index);
        setFormData(prev => ({ ...prev, items: newItems }));
    };

    const handleNavigateToAddProduct = () => {
        if (onNavigateToAddProduct && typeof onNavigateToAddProduct === 'function') {
            onNavigateToAddProduct();
        } else {
            showAlert('Navigation function not provided', 'warning');
        }
    };

    const generateQuotationNumber = () => {
        const prefix = 'QUO';
        const date = new Date();
        const timestamp = date.getTime().toString().slice(-6);
        return `${prefix}-${date.getFullYear()}-${timestamp}`;
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 2
        }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        return new Date(dateString).toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const calculateCGSTSGST = (taxableAmount, gstRate) => {
        const totalGSTAmount = (taxableAmount * gstRate) / 100;
        const cgstAmount = totalGSTAmount / 2;
        const sgstAmount = totalGSTAmount / 2;
        return { cgstAmount, sgstAmount, totalGSTAmount };
    };

    const calculateItemTotalWithGST = (item, discountPercentage = 0) => {
        const itemDiscountAmount = (item.amount * discountPercentage) / 100;
        const itemTaxableAmount = item.amount - itemDiscountAmount;
        if (formData.quotationType === 'with_gst') {
            const { totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
            return itemTaxableAmount + totalGSTAmount;
        }
        return itemTaxableAmount;
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setError('');

        if (!formData.customerName.trim()) {
            setError('Customer name is required');
            return;
        }

        if (formData.quotationType === 'with_gst' && !formData.customerGST.trim()) {
            setError('Customer GST number is required for GST quotations');
            return;
        }

        const validItems = formData.items.filter(item =>
            item.name.trim() && item.quantity > 0 && item.rate > 0
        );

        if (validItems.length === 0) {
            setError('At least one item is required. Please add products or manual items.');
            return;
        }

        setStep(2);
    };

    const handleSubmit = async () => {
        setError('');
        setIsSubmitting(true);

        try {
            const validItems = formData.items.filter(item =>
                item.name.trim() && item.quantity > 0 && item.rate > 0
            );

            const quotationData = {
                quotationNumber: quotationNumber,
                customerName: formData.customerName.trim(),
                customerEmail: formData.customerEmail.trim(),
                customerPhone: formData.customerPhone.trim(),
                customerAddress: formData.customerAddress.trim(),
                customerGST: formData.customerGST.trim(),
                items: validItems,
                subtotal: formData.subtotal,
                discount: parseFloat(formData.discount) || 0,
                quotationType: formData.quotationType,
                totalTaxAmount: formData.totalTaxAmount,
                totalAmount: formData.totalAmount,
                createdAt: Timestamp.fromDate(new Date(formData.quotationDate)),
                notes: formData.notes.trim(),
                terms: formData.terms.trim(),
                status: 'active',
                vendorId: vendorProfile.uid
            };

            const result = await createQuotation(quotationData);
            showAlert('Quotation created successfully!', 'success');
            onSubmit?.(result);
            if (onClose) onClose();

        } catch (error) {
            console.error('Error creating quotation:', error);
            setError(`Failed to create quotation: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    const getGstBreakdown = () => {
        const gstBreakdown = {};
        formData.items.forEach(item => {
            const rate = item.gstRate || 0;
            const discountPercentage = parseFloat(formData.discount) || 0;
            const itemTaxableAmount = (item.amount || 0) - ((item.amount || 0) * discountPercentage) / 100;
            const { cgstAmount, sgstAmount, totalGSTAmount } = calculateCGSTSGST(itemTaxableAmount, rate);

            if (rate > 0) {
                if (!gstBreakdown[rate]) {
                    gstBreakdown[rate] = { taxableAmount: 0, cgstAmount: 0, sgstAmount: 0, totalGSTAmount: 0 };
                }
                gstBreakdown[rate].taxableAmount += itemTaxableAmount;
                gstBreakdown[rate].cgstAmount += cgstAmount;
                gstBreakdown[rate].sgstAmount += sgstAmount;
                gstBreakdown[rate].totalGSTAmount += totalGSTAmount;
            }
        });
        return gstBreakdown;
    };

    const getPrimaryBankAccount = () => {
        return bankAccounts.find(account => account.isPrimary) || bankAccounts[0];
    };

    // Preview Step
    // Preview Step
    if (step === 2) {
        const discountAmount = (formData.subtotal * (parseFloat(formData.discount) || 0)) / 100;
        const gstBreakdown = getGstBreakdown();
        const primaryBank = getPrimaryBankAccount();

        const content = (
            <div className="bg-white">
                {/* Print Styles */}
<style jsx>{`
  @media print {
    /* Force exact colors and better rendering */
    * {
      -webkit-print-color-adjust: exact !important;
      color-adjust: exact !important;
      box-sizing: border-box !important;
    }
    
    /* Hide everything first, then show only print content */
    body * {
      visibility: hidden;
    }
    
    .print-content, .print-content * {
      visibility: visible;
    }
    
    /* Print content positioning and sizing */
    .print-content {
      position: absolute !important;
      left: 0 !important;
      top: 0 !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 15px !important;
      font-size: 12px !important;
      line-height: 1.3 !important;
      font-family: Arial, sans-serif !important;
      background: white !important;
    }
    
    /* Hide non-print elements */
    .no-print {
      display: none !important;
    }
    
    /* Page setup for A4 */
    @page {
      size: A4;
      margin: 0.4in;
    }
    
    /* Heading sizes - scaled down proportionally */
    .print-content h1 {
      font-size: 24px !important;
      margin: 0 0 10px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h2 {
      font-size: 18px !important;
      margin: 0 0 8px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h3 {
      font-size: 16px !important;
      margin: 0 0 6px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h4 {
      font-size: 14px !important;
      margin: 0 0 4px 0 !important;
      font-weight: bold !important;
    }
    
    .print-content h5 {
      font-size: 12px !important;
      margin: 0 0 3px 0 !important;
      font-weight: bold !important;
    }
    
    /* Paragraph and text sizing */
    .print-content p {
      margin: 0 0 3px 0 !important;
      font-size: 11px !important;
    }
    
    .print-content div {
      margin: 0 !important;
    }
    
    /* Table optimizations - keep structure, reduce size */
    .print-content table {
      width: 100% !important;
      border-collapse: collapse !important;
      margin: 8px 0 !important;
      font-size: 10px !important;
      page-break-inside: auto !important;
    }
    
    .print-content thead {
      display: table-header-group !important;
    }
    
    .print-content tbody {
      display: table-row-group !important;
    }
    
    .print-content th {
      background-color: #f8f9fa !important;
      border: 1px solid #dee2e6 !important;
      padding: 4px 3px !important;
      font-size: 9px !important;
      font-weight: bold !important;
      text-align: center !important;
      vertical-align: middle !important;
      line-height: 1.1 !important;
    }
    
    .print-content td {
      border: 1px solid #dee2e6 !important;
      padding: 3px 2px !important;
      font-size: 10px !important;
      vertical-align: top !important;
      line-height: 1.2 !important;
    }
    
    /* Reduce spacing in existing layout classes */
    .print-content .mb-8 {
      margin-bottom: 8px !important;
    }
    
    .print-content .mb-6 {
      margin-bottom: 6px !important;
    }
    
    .print-content .mb-4 {
      margin-bottom: 4px !important;
    }
    
    .print-content .mb-3 {
      margin-bottom: 3px !important;
    }
    
    .print-content .mb-2 {
      margin-bottom: 2px !important;
    }
    
    .print-content .mt-8 {
      margin-top: 8px !important;
    }
    
    .print-content .mt-6 {
      margin-top: 6px !important;
    }
    
    .print-content .mt-4 {
      margin-top: 4px !important;
    }
    
    .print-content .mt-3 {
      margin-top: 3px !important;
    }
    
    .print-content .mt-2 {
      margin-top: 2px !important;
    }
    
    /* Padding reductions */
    .print-content .py-4 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-3 {
      padding-top: 2px !important;
      padding-bottom: 2px !important;
    }
    
    .print-content .py-2 {
      padding-top: 1px !important;
      padding-bottom: 1px !important;
    }
    
    .print-content .px-4 {
      padding-left: 3px !important;
      padding-right: 3px !important;
    }
    
    .print-content .px-3 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    .print-content .px-2 {
      padding-left: 2px !important;
      padding-right: 2px !important;
    }
    
    /* Compact existing sections without changing structure */
    .print-content .p-8 {
      padding: 12px !important;
    }
    
    .print-content .p-6 {
      padding: 8px !important;
    }
    
    .print-content .p-4 {
      padding: 6px !important;
    }
    
    /* Text size adjustments */
    .print-content .text-3xl {
      font-size: 24px !important;
    }
    
    .print-content .text-2xl {
      font-size: 20px !important;
    }
    
    .print-content .text-xl {
      font-size: 18px !important;
    }
    
    .print-content .text-lg {
      font-size: 16px !important;
    }
    
    .print-content .text-base {
      font-size: 12px !important;
    }
    
    .print-content .text-sm {
      font-size: 11px !important;
    }
    
    .print-content .text-xs {
      font-size: 9px !important;
    }
    
    /* Ensure colors print properly */
    .print-content .text-gray-900 {
      color: #000 !important;
    }
    
    .print-content .text-gray-800 {
      color: #1f2937 !important;
    }
    
    .print-content .text-gray-700 {
      color: #374151 !important;
    }
    
    .print-content .text-gray-600 {
      color: #4b5563 !important;
    }
    
    .print-content .text-gray-500 {
      color: #6b7280 !important;
    }
    
    .print-content .text-purple-900 {
      color: #581c87 !important;
    }
    
    .print-content .text-red-600 {
      color: #dc2626 !important;
    }
    
    .print-content .text-orange-600 {
      color: #ea580c !important;
    }
    
    .print-content .text-green-600 {
      color: #16a34a !important;
    }
    
    .print-content .text-blue-600 {
      color: #2563eb !important;
    }
    
    /* Background colors for sections */
    .print-content .bg-gray-50 {
      background-color: #f9fafb !important;
    }
    
    .print-content .bg-blue-50 {
      background-color: #eff6ff !important;
    }
    
    .print-content .bg-purple-50 {
      background-color: #faf5ff !important;
    }
    
    .print-content .bg-yellow-50 {
      background-color: #fefce8 !important;
    }
    
    /* Borders */
    .print-content .border {
      border: 1px solid #d1d5db !important;
    }
    
    .print-content .border-gray-200 {
      border-color: #e5e7eb !important;
    }
    
    .print-content .border-gray-300 {
      border-color: #d1d5db !important;
    }
    
    .print-content .border-t {
      border-top: 1px solid #d1d5db !important;
    }
    
    .print-content .border-b {
      border-bottom: 1px solid #d1d5db !important;
    }
    
    .print-content .border-l {
      border-left: 1px solid #d1d5db !important;
    }
    
    .print-content .border-r {
      border-right: 1px solid #d1d5db !important;
    }
    
    /* Flex layouts - keep structure */
    .print-content .flex {
      display: flex !important;
    }
    
    .print-content .justify-between {
      justify-content: space-between !important;
    }
    
    .print-content .items-center {
      align-items: center !important;
    }
    
    .print-content .items-start {
      align-items: flex-start !important;
    }
    
    .print-content .space-x-6 > * + * {
      margin-left: 10px !important;
    }
    
    .print-content .space-x-3 > * + * {
      margin-left: 6px !important;
    }
    
    .print-content .space-x-2 > * + * {
      margin-left: 4px !important;
    }
    
    .print-content .space-y-2 > * + * {
      margin-top: 2px !important;
    }
    
    .print-content .space-y-1 > * + * {
      margin-top: 1px !important;
    }
    
    /* QR code sizing */
    .print-content img {
      max-width: 100% !important;
      height: auto !important;
    }
    
    /* Specific QR code adjustments */
    .print-content .w-48 {
      width: 80px !important;
    }
    
    .print-content .h-40 {
      height: 60px !important;
    }
    
    /* Grid layouts */
    .print-content .grid {
      display: grid !important;
    }
    
    .print-content .grid-cols-1 {
      grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
    }
    
    .print-content .grid-cols-2 {
      grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
    }
    
    .print-content .gap-6 {
      gap: 8px !important;
    }
    
    .print-content .gap-4 {
      gap: 6px !important;
    }
    
    /* Badges and small elements */
    .print-content .rounded-full {
      border-radius: 9999px !important;
    }
    
    .print-content .rounded-md {
      border-radius: 6px !important;
    }
    
    .print-content .rounded {
      border-radius: 4px !important;
    }
    
    /* Font weights */
    .print-content .font-bold {
      font-weight: 700 !important;
    }
    
    .print-content .font-semibold {
      font-weight: 600 !important;
    }
    
    .print-content .font-medium {
      font-weight: 500 !important;
    }
    
    /* Ensure table responsiveness is disabled in print */
    .print-content .overflow-x-auto {
      overflow-x: visible !important;
    }
    
    /* Make sure flex items don't shrink too much */
    .print-content .flex-1 {
      flex: 1 1 0% !important;
    }
    
    .print-content .flex-shrink-0 {
      flex-shrink: 0 !important;
    }
    
    /* Text alignment */
    .print-content .text-left {
      text-align: left !important;
    }
    
    .print-content .text-center {
      text-align: center !important;
    }
    
    .print-content .text-right {
      text-align: right !important;
    }
    
    /* Width constraints */
    .print-content .w-full {
      width: 100% !important;
    }
    
    .print-content .w-96 {
      width: 300px !important;
    }
    
    /* Handle very long content */
    .print-content .whitespace-pre-wrap {
      white-space: pre-wrap !important;
      word-wrap: break-word !important;
    }
    
    /* Ensure content doesn't break poorly */
    .print-content .page-break-inside-avoid {
      page-break-inside: avoid !important;
    }
    
    /* Make sure all text is readable */
    .print-content {
      color: #000 !important;
    }
    
    /* Specific adjustments for very small elements */
    .print-content .min-w-full {
      min-width: 100% !important;
    }
    
    .print-content .divide-y > * {
      border-top-width: 1px !important;
      border-color: #e5e7eb !important;
    }
    
    .print-content .divide-gray-200 > * {
      border-color: #e5e7eb !important;
    }
  }
`}</style>

                {/* Header Actions */}
                <div className="flex justify-between items-center mb-6 no-print">
                    <div>
                        <h3 className="text-lg font-medium text-gray-900">Quotation Preview</h3>
                    </div>
                    <div className="flex space-x-3">
                        <button
                            type="button"
                            onClick={() => setStep(1)}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                            </svg>
                            Back to Edit
                        </button>
                        <button
                            type="button"
                            onClick={handlePrint}
                            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                        >
                            <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                            </svg>
                            Print
                        </button>
                        {isModal && (
                            <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {error && (
                    <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md no-print">
                        <p className="text-sm text-red-800">{error}</p>
                    </div>
                )}

                <div className="print-content bg-white border border-gray-200 rounded-lg p-8">
                    {/* Quotation Header */}
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-purple-900 mb-2">QUOTATION</h1>
                            <div className="text-sm text-gray-600">
                                <p><strong>Quotation #:</strong> {quotationNumber}</p>
                                <p><strong>Date:</strong> {formatDate(formData.quotationDate)}</p>
                                <p><strong>Status:</strong> <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">ACTIVE</span></p>
                            </div>
                        </div>

                        {formData.quotationType === 'with_gst' && (
                            <div className="text-right">
                                <h2 className="text-xl font-bold text-gray-900 mb-2">
                                    {vendorProfile?.shopName || 'Your Shop Name'}
                                </h2>
                                <div className="text-sm text-gray-600">
                                    <p>{vendorProfile?.address || 'Shop Address'}</p>
                                    <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                                    <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                                    <p>Email: {vendorProfile?.email || 'N/A'}</p>
                                    {vendorProfile?.gstNumber && (
                                        <p>GST: {vendorProfile.gstNumber}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quote To Section */}
                    <div className="mb-8">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3">Quote To:</h3>
                        <div className="bg-gray-50 p-4 rounded-md">
                            <div className="text-sm">
                                <p className="font-medium text-gray-900">{formData.customerName}</p>
                                {formData.customerEmail && <p className="text-gray-600">{formData.customerEmail}</p>}
                                {formData.customerPhone && <p className="text-gray-600">{formData.customerPhone}</p>}
                                {formData.quotationType === 'with_gst' && formData.customerGST && (
                                    <p className="text-gray-600">GST: {formData.customerGST}</p>
                                )}
                                {formData.customerAddress && <p className="text-gray-600 mt-2">{formData.customerAddress}</p>}
                            </div>
                        </div>
                    </div>

                    {/* Items Table */}
                    <div className="mb-8">
                        <h4 className="text-lg font-semibold text-gray-900 mb-4">Quotation Items</h4>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Description</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">HSN</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Qty</th>
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Rate (₹)</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">GST (%)</th>
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">CGST (₹)</th>
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">SGST (₹)</th>
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Amount (₹)</th>
                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total (₹)</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {formData.items.filter(item => item.name.trim() && item.quantity > 0 && item.rate > 0).map((item, index) => {
                                        const discountPercentage = parseFloat(formData.discount) || 0;
                                        const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                                        const itemTaxableAmount = item.amount - itemDiscountAmount;
                                        const { cgstAmount, sgstAmount } = calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0);
                                        const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                                        return (
                                            <tr key={index}>
                                                <td className="px-3 py-4 border-r">
                                                    <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                                    {item.productId && <div className="text-xs text-gray-500">ID: {item.productId}</div>}
                                                </td>
                                                <td className="px-2 py-4 text-center border-r">
                                                    <span className="text-xs text-gray-900">{item.hsnCode || 'N/A'}</span>
                                                </td>
                                                <td className="px-2 py-4 text-center border-r">
                                                    <span className="text-sm text-gray-900">{item.quantity}</span>
                                                </td>
                                                <td className="px-2 py-4 text-right border-r">
                                                    <span className="text-sm text-gray-900">{formatCurrency(item.rate)}</span>
                                                </td>
                                                <td className="px-2 py-4 text-center border-r">
                                                    <span className="text-sm text-gray-900">{item.gstRate || 0}%</span>
                                                </td>
                                                <td className="px-2 py-4 text-right border-r">
                                                    <span className="text-xs text-gray-900">{formatCurrency(cgstAmount)}</span>
                                                </td>
                                                <td className="px-2 py-4 text-right border-r">
                                                    <span className="text-xs text-gray-900">{formatCurrency(sgstAmount)}</span>
                                                </td>
                                                <td className="px-2 py-4 text-right border-r">
                                                    <span className="text-sm font-medium text-gray-900">{formatCurrency(item.amount)}</span>
                                                </td>
                                                <td className="px-3 py-4 text-right">
                                                    <span className="text-sm font-bold text-gray-900">{formatCurrency(totalWithGST)}</span>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Combined Totals and Bank Details Section - PREVIEW */}
                    <div className="flex justify-between mb-8 space-x-6">
                        {/* Bank Details Section */}
                        {primaryBank ? (
                            <div className="flex-1">
                                <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                                    <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                                        <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                        </svg>
                                        Bank Details
                                        {primaryBank.isPrimary && (
                                            <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                Primary
                                            </span>
                                        )}
                                    </h5>

                                    <div className="flex">
                                        <div className="flex-1 space-y-2 text-sm">
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">Account Holder:</span>
                                                <span className="font-semibold text-gray-900">{primaryBank.accountHolderName}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">Account Number:</span>
                                                <span className="font-mono font-semibold text-gray-900">{primaryBank.accountNumber}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">IFSC Code:</span>
                                                <span className="font-mono font-semibold text-gray-900">{primaryBank.ifscCode}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">Bank Name:</span>
                                                <span className="font-semibold text-gray-900 text-right">{primaryBank.bankName}</span>
                                            </div>
                                            {primaryBank.branchName && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">Branch:</span>
                                                    <span className="font-semibold text-gray-900 text-right">{primaryBank.branchName}</span>
                                                </div>
                                            )}
                                            <div className="flex justify-between items-center">
                                                <span className="text-gray-600 font-medium">Account Type:</span>
                                                <span className="font-semibold text-gray-900 capitalize">{primaryBank.accountType}</span>
                                            </div>
                                            {primaryBank.upiId && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">UPI ID:</span>
                                                    <span className="font-mono font-semibold text-gray-900">{primaryBank.upiId}</span>
                                                </div>
                                            )}
                                        </div>

                                        {/* UPI QR Code */}
                                        {primaryBank.upiScannerURL && (
                                            <div className="ml-4 flex flex-col items-center">
                                                <p className="text-xs font-medium text-blue-700 mb-2">UPI QR Code</p>
                                                <img
                                                    src={primaryBank.upiScannerURL}
                                                    alt="UPI QR Code"
                                                    className="w-48 h-40 object-cover rounded border-2 border-blue-300 shadow-sm"
                                                />
                                                <p className="text-xs text-blue-600 mt-1 text-center font-medium">Scan to Pay</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="flex-1">
                                <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
                                    <h5 className="text-md font-semibold text-gray-900 mb-3">Bank Details</h5>
                                    <p className="text-sm text-gray-600">No bank details available</p>
                                </div>
                            </div>
                        )}

                        {/* Totals Section */}
                        <div className="flex-1">
                            <div className="bg-gray-50 p-4 rounded-md border">


                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Subtotal:</span>
                                        <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.subtotal)}</span>
                                    </div>

                                    {formData.discount > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Discount ({formData.discount}%):</span>
                                            <span className="text-sm font-medium text-gray-900">-{formatCurrency(discountAmount)}</span>
                                        </div>
                                    )}

                                    {formData.quotationType === 'with_gst' && Object.keys(gstBreakdown).length > 0 && (
                                        <div className="border-t border-gray-200 pt-2">
                                            <div className="text-xs font-medium text-gray-700 mb-1">GST Breakdown:</div>
                                            {Object.entries(gstBreakdown).map(([rate, data]) => (
                                                <div key={rate} className="space-y-1 mb-2">
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-600">CGST @ {rate / 2}%:</span>
                                                        <span className="text-xs font-medium text-gray-900">{formatCurrency(data.cgstAmount)}</span>
                                                    </div>
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-xs text-gray-600">SGST @ {rate / 2}%:</span>
                                                        <span className="text-xs font-medium text-gray-900">{formatCurrency(data.sgstAmount)}</span>
                                                    </div>
                                                </div>
                                            ))}
                                            <div className="flex justify-between items-center border-t border-gray-200 mt-1 pt-1">
                                                <span className="text-sm text-gray-600">Total GST:</span>
                                                <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.totalTaxAmount)}</span>
                                            </div>
                                        </div>
                                    )}

                                    <div className="border-t border-gray-200 mt-3 pt-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-semibold text-gray-900">Total:</span>
                                            <span className="text-lg font-bold text-purple-900">{formatCurrency(formData.totalAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Notes and Terms */}
                    {(formData.notes || formData.terms) && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            {formData.notes && (
                                <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Notes:</h5>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.notes}</p>
                                </div>
                            )}

                            {formData.terms && (
                                <div>
                                    <h5 className="text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</h5>
                                    <p className="text-sm text-gray-600 whitespace-pre-wrap">{formData.terms}</p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Footer */}
                    <div className="border-t border-gray-200 pt-6">
                        <div className="text-center text-sm text-gray-500">
                            <p>Thank you for considering our quotation!</p>
                            {formData.quotationType === 'with_gst' && (
                                <p className="mt-2">This is a computer generated quotation.</p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Preview Actions */}
                <div className="flex justify-end space-x-3 mt-6 no-print">
                    <button
                        type="button"
                        onClick={() => setStep(1)}
                        disabled={isSubmitting}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                        Edit Quotation
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={isSubmitting}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50 flex items-center"
                    >
                        {isSubmitting ? (
                            <>
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Creating...
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Create Quotation
                            </>
                        )}
                    </button>
                </div>
            </div>
        );

        if (isModal) {
            return (
                <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                    <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
                        {content}
                    </div>
                </div>
            );
        }

        return <div className="p-6">{content}</div>;
    }

    // Form Step (Step 1)
    const content = (
        <div className="bg-white">
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-lg font-medium text-gray-900">Create Quotation</h3>
                </div>
                {isModal && (
                    <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                )}
            </div>

            {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-md">
                    <p className="text-sm text-red-800">{error}</p>
                </div>
            )}

            <form onSubmit={handleFormSubmit}>
                <div className="bg-white border border-gray-200 rounded-lg p-8">
                    <div className="flex justify-between items-start mb-8">
                        <div>
                            <h1 className="text-3xl font-bold text-purple-900 mb-2">QUOTATION</h1>
                            <div className="text-sm text-gray-600">
                                <p><strong>Quotation #:</strong> {quotationNumber}</p>
                                <p><strong>Date:</strong> {formatDate(formData.quotationDate)}</p>

                            </div>
                        </div>

                        {formData.quotationType === 'with_gst' && (
                            <div className="text-right">
                                <h2 className="text-xl font-bold text-gray-900 mb-2">
                                    {vendorProfile?.shopName || 'Your Shop Name'}
                                </h2>
                                <div className="text-sm text-gray-600">
                                    <p>{vendorProfile?.address || 'Shop Address'}</p>
                                    <p>{vendorProfile?.city || 'City'}, {vendorProfile?.state || 'State'} {vendorProfile?.pincode || 'PIN'}</p>
                                    <p>Phone: {vendorProfile?.phone || 'N/A'}</p>
                                    <p>Email: {vendorProfile?.email || 'N/A'}</p>
                                    {vendorProfile?.gstNumber && (
                                        <p>GST: {vendorProfile.gstNumber}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Quotation Configuration */}
                    <div className="mb-6 bg-purple-50 p-4 rounded-lg">
                        <h4 className="text-md font-medium text-gray-900 mb-3">Quotation Configuration</h4>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-2">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Quotation Type</label>
                                <select
                                    value={formData.quotationType}
                                    onChange={(e) => handleInputChange('quotationType', e.target.value)}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                >
                                    <option value="with_gst">With GST</option>
                                    <option value="without_gst">Without GST</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-3">
                            <h3 className="text-lg font-semibold text-gray-900">Quote To:</h3>
                            <button
                                type="button"
                                onClick={handleAddCustomer}
                                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                            >
                                <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                                Add Customer
                            </button>
                        </div>
                        <div className="bg-gray-50 p-4 rounded-md border-2 border-dashed border-gray-300">
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                <div className="relative">
                                    <label className="block text-sm font-medium text-gray-700">Customer Name *</label>
                                    <input
                                        type="text"
                                        required
                                        value={formData.customerName}
                                        onChange={(e) => handleCustomerSearch(e.target.value)}
                                        onFocus={() => {
                                            if (formData.customerName.trim() && filteredCustomers.length > 0) {
                                                setShowCustomerDropdown(true);
                                            }
                                        }}
                                        onBlur={() => {
                                            setTimeout(() => setShowCustomerDropdown(false), 200);
                                        }}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="Search or enter customer name"
                                    />

                                    {showCustomerDropdown && filteredCustomers.length > 0 && (
                                        <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                                            {filteredCustomers.map((customer) => (
                                                <div
                                                    key={customer.id}
                                                    className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-purple-50"
                                                    onClick={() => handleCustomerSelect(customer)}
                                                >
                                                    <div className="flex items-center">
                                                        <span className="font-medium block truncate">
                                                            {customer.name}
                                                        </span>
                                                    </div>
                                                    <div className="text-gray-500 text-xs">
                                                        {customer.phone && <span>{customer.phone}</span>}
                                                        {customer.phone && customer.email && <span> • </span>}
                                                        {customer.email && <span>{customer.email}</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email</label>
                                    <input
                                        type="email"
                                        value={formData.customerEmail}
                                        onChange={(e) => handleInputChange('customerEmail', e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="customer@example.com"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Phone</label>
                                    <input
                                        type="tel"
                                        value={formData.customerPhone}
                                        onChange={(e) => handleInputChange('customerPhone', e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                        placeholder="+91 9876543210"
                                    />
                                </div>
                                {formData.quotationType === 'with_gst' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">GST Number *</label>
                                        <input
                                            type="text"
                                            required={formData.quotationType === 'with_gst'}
                                            value={formData.customerGST}
                                            onChange={(e) => handleInputChange('customerGST', e.target.value)}
                                            className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                            placeholder="Enter GST number"
                                        />
                                    </div>
                                )}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Quotation Date *</label>
                                    <input
                                        type="date"
                                        required
                                        value={formData.quotationDate}
                                        onChange={(e) => handleInputChange('quotationDate', e.target.value)}
                                        className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    />
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="block text-sm font-medium text-gray-700">Address</label>
                                <textarea
                                    rows={3}
                                    value={formData.customerAddress}
                                    onChange={(e) => handleInputChange('customerAddress', e.target.value)}
                                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                                    placeholder="Customer address"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mb-8">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="text-lg font-semibold text-gray-900">Quotation Items</h4>
                            <div className="flex space-x-2">
                                {products.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={addProductItem}
                                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-purple-600 hover:bg-purple-700"
                                    >
                                        <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Add Product
                                    </button>
                                )}
                                <button
                                    type="button"
                                    onClick={addManualItem}
                                    className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                                >
                                    <svg className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
                                    Add Manual Item
                                </button>
                            </div>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200 border border-gray-300">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Description</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">HSN</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Qty</th>
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Rate (₹)</th>
                                        {formData.quotationType === 'with_gst' && (
                                            <>
                                                <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r">GST (%)</th>
                                                <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">CGST (₹)</th>
                                                <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">SGST (₹)</th>
                                            </>
                                        )}
                                        <th className="px-2 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Amount (₹)</th>
                                        <th className="px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border-r">Total (₹)</th>
                                        <th className="px-2 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {formData.items.length === 0 ? (
                                        <tr>
                                            <td colSpan={formData.quotationType === 'with_gst' ? "10" : "7"} className="px-6 py-8 text-center text-gray-500">
                                                No items added yet. Click "Add Product" or "Add Manual Item" to get started.
                                            </td>
                                        </tr>
                                    ) : (
                                        formData.items.map((item, index) => {
                                            const discountPercentage = parseFloat(formData.discount) || 0;
                                            const itemDiscountAmount = (item.amount * discountPercentage) / 100;
                                            const itemTaxableAmount = item.amount - itemDiscountAmount;
                                            const { cgstAmount, sgstAmount } = formData.quotationType === 'with_gst' ?
                                                calculateCGSTSGST(itemTaxableAmount, item.gstRate || 0) :
                                                { cgstAmount: 0, sgstAmount: 0 };
                                            const totalWithGST = calculateItemTotalWithGST(item, discountPercentage);

                                            return (
                                                <tr key={index} className="hover:bg-gray-50">
                                                    <td className="px-3 py-4 border-r">
                                                        {item.isManual ? (
                                                            <input
                                                                type="text"
                                                                value={item.name}
                                                                onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                                                                placeholder="Enter item name"
                                                                className="w-full px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                                            />
                                                        ) : (
                                                            <div className="space-y-2">
                                                                <div className="flex items-center space-x-2">
                                                                    {item.productId ? (
                                                                        <div className="flex-1">
                                                                            <div className="text-sm font-medium text-gray-900">{item.name}</div>
                                                                            <div className="text-xs text-gray-500">ID: {item.productId}</div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="flex-1 text-gray-500 italic text-sm">No product</div>
                                                                    )}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => handleProductModalOpen()}
                                                                        className="px-2 py-1 border border-gray-300 rounded text-xs font-medium text-gray-700 bg-white hover:bg-gray-50"
                                                                    >
                                                                        {item.productId ? 'Change' : 'Select'}
                                                                    </button>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </td>
                                                    <td className="px-2 py-4 text-center border-r">
                                                        <input
                                                            type="text"
                                                            value={item.hsnCode}
                                                            onChange={(e) => handleItemChange(index, 'hsnCode', e.target.value)}
                                                            placeholder="HSN"
                                                            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-4 text-center border-r">
                                                        <input
                                                            type="number"
                                                            min="1"
                                                            value={item.quantity}
                                                            onChange={(e) => handleItemChange(index, 'quantity', e.target.value)}
                                                            className="w-16 px-1 py-1 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500"
                                                        />
                                                    </td>
                                                    <td className="px-2 py-4 text-right border-r">
                                                        <input
                                                            type="number"
                                                            step="0.01"
                                                            min="0"
                                                            value={item.rate}
                                                            onChange={(e) => handleItemChange(index, 'rate', e.target.value)}
                                                            disabled={!item.isManual && item.productId}
                                                            className="w-20 px-1 py-1 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 disabled:bg-gray-100"
                                                        />
                                                    </td>
                                                    {formData.quotationType === 'with_gst' && (
                                                        <>
                                                            <td className="px-2 py-4 text-center border-r relative">
                                                                <div className="relative inline-block">
                                                                    <select
                                                                        value={item.gstRate || 0}
                                                                        onChange={(e) => handleItemChange(index, 'gstRate', e.target.value)}
                                                                        className="appearance-none w-20 px-2 py-1 pr-6 border border-gray-300 rounded text-xs text-center focus:outline-none focus:ring-1 focus:ring-purple-500 focus:border-purple-500 bg-white cursor-pointer"
                                                                    >
                                                                        <option value={0}>0%</option>
                                                                        <option value={3}>3%</option>
                                                                        <option value={5}>5%</option>
                                                                        <option value={12}>12%</option>
                                                                        <option value={18}>18%</option>
                                                                        <option value={28}>28%</option>
                                                                    </select>
                                                                </div>
                                                            </td>
                                                            <td className="px-2 py-4 text-right border-r">
                                                                <span className="text-xs font-medium text-gray-900">
                                                                    {formatCurrency(cgstAmount)}
                                                                </span>
                                                            </td>
                                                            <td className="px-2 py-4 text-right border-r">
                                                                <span className="text-xs font-medium text-gray-900">
                                                                    {formatCurrency(sgstAmount)}
                                                                </span>
                                                            </td>
                                                        </>
                                                    )}
                                                    <td className="px-2 py-4 text-right border-r">
                                                        <span className="text-sm font-medium text-gray-900">
                                                            {formatCurrency(item.amount)}
                                                        </span>
                                                    </td>
                                                    <td className="px-3 py-4 text-right border-r">
                                                        <span className="text-sm font-bold text-gray-900">
                                                            {formatCurrency(totalWithGST)}
                                                        </span>
                                                    </td>
                                                    <td className="px-2 py-4 text-center">
                                                        {formData.items.length > 1 && (
                                                            <button
                                                                type="button"
                                                                onClick={() => removeItem(index)}
                                                                className="text-red-600 hover:text-red-900"
                                                                title="Remove item"
                                                            >
                                                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                </svg>
                                                            </button>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>

                    {/* Combined Totals and Bank Details Section - Fixed UI Layout */}
                    <div className="flex justify-between mb-6 space-x-6">
                        {/* Bank Details Section */}
                        {(() => {
                            const primaryBank = getPrimaryBankAccount();
                            return primaryBank ? (
                                <div className="flex-1">
                                    <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
                                        <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                                            <svg className="h-4 w-4 mr-2 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                                            </svg>
                                            Bank Details
                                            {primaryBank.isPrimary && (
                                                <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                                    Primary
                                                </span>
                                            )}
                                        </h5>

                                        <div className="flex">
                                            {/* Bank details in proper vertical layout */}
                                            <div className="flex-1 space-y-2 text-sm">
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">Account Holder:</span>
                                                    <span className="font-semibold text-gray-900">{primaryBank.accountHolderName}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">Account Number:</span>
                                                    <span className="font-mono font-semibold text-gray-900">{primaryBank.accountNumber}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">IFSC Code:</span>
                                                    <span className="font-mono font-semibold text-gray-900">{primaryBank.ifscCode}</span>
                                                </div>
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">Bank Name:</span>
                                                    <span className="font-semibold text-gray-900 text-right">{primaryBank.bankName}</span>
                                                </div>
                                                {primaryBank.branchName && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 font-medium">Branch:</span>
                                                        <span className="font-semibold text-gray-900 text-right">{primaryBank.branchName}</span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center">
                                                    <span className="text-gray-600 font-medium">Account Type:</span>
                                                    <span className="font-semibold text-gray-900 capitalize">{primaryBank.accountType}</span>
                                                </div>
                                                {primaryBank.upiId && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-gray-600 font-medium">UPI ID:</span>
                                                        <span className="font-mono font-semibold text-gray-900">{primaryBank.upiId}</span>
                                                    </div>
                                                )}
                                            </div>

                                            {/* UPI QR Code */}
                                            {primaryBank.upiScannerURL && (
                                                <div className="ml-2 flex flex-col items-center">
                                                    <p className="text-xs font-medium text-blue-700 mb-2">UPI QR Code</p>
                                                    <img
                                                        src={primaryBank.upiScannerURL}
                                                        alt="UPI QR Code"
                                                        className="w-48 h-40 object-cover rounded border-2 border-blue-300 shadow-sm"
                                                    />
                                                    <p className="text-xs text-blue-600 mt-1 text-center">Scan to Pay</p>
                                                </div>
                                            )}
                                        </div>


                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1">
                                    <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                                        <h5 className="text-md font-semibold text-gray-900 mb-3 flex items-center">
                                            <svg className="h-4 w-4 mr-2 text-yellow-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                            </svg>
                                            No Bank Details Found
                                        </h5>
                                        <div className="space-y-3">
                                            <p className="text-sm text-gray-600">
                                                No bank account found in your profile. Bank details are required to include payment information in quotations.
                                            </p>
                                            <div className="bg-white border border-yellow-300 rounded-md p-2">
                                                <p className="text-xs text-gray-700 mb-2">To add bank details:</p>
                                                <ul className="text-xs text-gray-600 space-y-1 mb-2">
                                                    <li>• Go to Profile → Bank Details</li>
                                                    <li>• Add your bank account</li>
                                                    <li>• Set one as primary</li>
                                                </ul>
                                            </div>
                                            <Link
                                                to="/profile?tab=bank"
                                                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 transition-colors"
                                            >
                                                <svg className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                                </svg>
                                                Add Bank Details
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            );
                        })()}

                        {/* Totals Section */}
                        <div className="flex-1">
                            <div className="bg-gray-50 p-4 rounded-md border">


                                <div className="space-y-2">
                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Subtotal:</span>
                                        <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.subtotal)}</span>
                                    </div>

                                    <div className="flex justify-between items-center">
                                        <span className="text-sm text-gray-600">Discount:</span>
                                        <div className="flex items-center space-x-2">
                                            <input
                                                type="number"
                                                step="0.01"
                                                min="0"
                                                max="100"
                                                value={formData.discount}
                                                onChange={(e) => handleInputChange('discount', e.target.value)}
                                                className="w-16 px-2 py-1 border border-gray-300 rounded text-xs text-right focus:outline-none focus:ring-1 focus:ring-purple-500"
                                                placeholder="0"
                                            />
                                            <span className="text-xs text-gray-600">%</span>
                                        </div>
                                    </div>

                                    {formData.discount > 0 && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Discount ({formData.discount}%):</span>
                                            <span className="text-sm font-medium text-gray-900">-{formatCurrency((formData.subtotal * formData.discount) / 100)}</span>
                                        </div>
                                    )}

                                    {formData.quotationType === 'with_gst' && (
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-600">Total GST:</span>
                                            <span className="text-sm font-medium text-gray-900">{formatCurrency(formData.totalTaxAmount)}</span>
                                        </div>
                                    )}

                                    <div className="border-t border-gray-200 mt-3 pt-3">
                                        <div className="flex justify-between items-center">
                                            <span className="text-lg font-semibold text-gray-900">Total:</span>
                                            <span className="text-lg font-bold text-purple-900">{formatCurrency(formData.totalAmount)}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Notes:</label>
                            <textarea
                                rows={4}
                                value={formData.notes}
                                onChange={(e) => handleInputChange('notes', e.target.value)}
                                placeholder="Any additional notes..."
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-semibold text-gray-900 mb-2">Terms & Conditions:</label>
                            <textarea
                                rows={4}
                                value={formData.terms}
                                onChange={(e) => handleInputChange('terms', e.target.value)}
                                className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-purple-500 focus:border-purple-500 sm:text-sm"
                            />
                        </div>
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                        <div className="text-center text-sm text-gray-500">
                            <p>Thank you for considering our quotation!</p>
                            {formData.quotationType === 'with_gst' && (
                                <p className="mt-2">This is a computer generated quotation.</p>
                            )}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={!formData.customerName.trim() || formData.totalAmount <= 0}
                        className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                    >
                        Preview Quotation
                    </button>
                </div>
            </form>

            <ProductSelectionModal
                isOpen={showProductModal}
                onClose={() => setShowProductModal(false)}
                products={products}
                onSelectProducts={handleProductSelect}
                selectedProducts={formData.items.filter(item => item.productId)}
                onAddManualItem={addManualItem}
                onNavigateToAddProduct={handleNavigateToAddProduct}
            />

            <CustomerModal
                isOpen={showCustomerModal}
                onClose={() => setShowCustomerModal(false)}
                onSave={handleCustomerSaved}
            />
        </div>
    );

    if (isModal) {
        return (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
                <div className="relative top-4 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white mb-8">
                    {content}
                </div>
            </div>
        );
    }

    return <div className="p-6">{content}</div>;
};

export default CreateQuotationModal;